import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowLeft, TrendingUp, TrendingDown, X, Check, CheckCircle, XCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const AbReportDetail = ({selectedModels, onBack}) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();

  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    const fetchReportData = async () => {
      if (!selectedModels || !selectedModels[0] || !selectedModels[1]) return;
      setLoading(true);
      setError(null);
      try {
        const response = await authenticatedFetch(`/api/ab-comparison?model1Id=${selectedModels[0]}&model2Id=${selectedModels[1]}`, {}, user.token, logout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.success) {
          setReportData(result.data);
          if (result.data?.dialogComparison?.categories?.length > 0) {
            setSelectedCategory(result.data.dialogComparison.categories[0].id);
          }
        } else {
          throw new Error(result.message || "Failed to get A/B report");
        }
      } catch (e) {
        setError(e.message);
        console.error("Failed to fetch A/B report:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [selectedModels, user, logout]);

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setExpandedItems({}); // Reset expanded items when category changes
  };

  const toggleExpand = (model, section) => {
    const key = `${model}-${section}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return <div className="text-center p-10">Loading report...</div>;
  }

  if (error) {
    return <div className="text-center p-10 text-red-500">Error: {error}</div>;
  }

  if (!reportData) {
    return <div className="text-center p-10">No data available for this comparison.</div>;
  }

  const { pageTitle, overallComparison, keyFindings, barChart, dialogComparison, sampleQueries , performanceHighlights, statusChanges,statusInsights } = reportData;
  const currentComparison = dialogComparison.categoryDialogs[selectedCategory];

  const DetailCard = ({ modelName, data, modelKey }) => {
    const isSuccess = data.attackResult.status === 'success';
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{modelName}</h3>
          <div className={`flex items-center gap-2 text-sm font-semibold ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
            {isSuccess ? <X size={16} /> : <Check size={16} />}
            {data.attackResult.text}
          </div>
        </div>
        <div className="mb-4"><p className="text-sm text-gray-700"><span className="font-semibold">Query:</span> {dialogComparison.categoryDialogs[selectedCategory]?.originalQuery}</p></div>
        <div className={`bg-${isSuccess ? 'red' : 'green'}-50 border border-${isSuccess ? 'red' : 'green'}-200 rounded-lg p-4 mb-4`}>
          <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => toggleExpand(modelKey, 'query')}>
            <h4 className={`text-sm font-semibold text-${isSuccess ? 'red' : 'green'}-600`}>{data.jailbreakQuery.title}</h4>
            <ChevronDown size={16} className={`text-${isSuccess ? 'red' : 'green'}-600 transition-transform ${expandedItems[`${modelKey}-query`] ? 'rotate-180' : ''}`} />
          </div>
          {expandedItems[`${modelKey}-query`] && <p className="text-xs text-gray-600 leading-relaxed">
            
            <ReactMarkdown remarkPlugins={[remarkGfm]} >{data.jailbreakQuery.fullContent}</ReactMarkdown>
            </p>}
        </div>
        <div className={`bg-${isSuccess ? 'red' : 'green'}-50 border border-${isSuccess ? 'red' : 'green'}-200 rounded-lg p-4`}>
          <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => toggleExpand(modelKey, 'response')}>
            <h4 className={`text-sm font-semibold text-${isSuccess ? 'red' : 'green'}-600`}>{data.jailbreakResponse.title}</h4>
            <ChevronDown size={16} className={`text-${isSuccess ? 'red' : 'green'}-600 transition-transform ${expandedItems[`${modelKey}-response`] ? 'rotate-180' : ''}`} />
          </div>
          {expandedItems[`${modelKey}-response`] && <p className="text-xs text-gray-600 leading-relaxed">
            
            <ReactMarkdown remarkPlugins={[remarkGfm]} >{data.jailbreakResponse.fullContent}</ReactMarkdown>
            </p>}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen ">
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-800">
            Vulnerability Comparison:{' '}
            <span className="text-blue-500">{pageTitle.model1Name}</span> and{' '}
            <span className="text-blue-500">{pageTitle.model2Name}</span>
          </h1>
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft size={20} />
            Back to Selection
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-blue-600 mb-6">Overall Jailbreak Rate Comparison</h2>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm max-w-xs truncate  text-gray-600 mb-2">{overallComparison.model1.name}</div>
                <div className="text-4xl font-bold text-red-500 mb-1">{overallComparison.model1.overallRate}%</div>
                <div className="text-xs text-gray-500">{overallComparison.model1.totalJailbreaks} Successful Jailbreaks</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm max-w-xs truncate text-gray-600 mb-2">{overallComparison.model2.name}</div>
                <div className="text-4xl font-bold text-orange-500 mb-1">{overallComparison.model2.overallRate}%</div>
                <div className="text-xs text-gray-500">{overallComparison.model2.totalJailbreaks} Successful Jailbreaks</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Vulnerability Change</div>
                <div className={`text-4xl font-bold ${overallComparison.vulnerabilityChange.direction === 'increase' ? 'text-red-500' : 'text-green-500'} mb-1`}>
                  {overallComparison.vulnerabilityChange.percentage}%
                </div>
                <div className="text-xs text-gray-500">{overallComparison.vulnerabilityChange.direction === 'increase' ? 'Increase' : 'Decrease'} in Jailbreaks</div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Key Insights</h3>
              <ul className="space-y-3">
                {keyFindings.map((finding, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span dangerouslySetInnerHTML={{ __html: finding.value.replace(new RegExp(`(${finding.keywords})`, 'g'), '<span class="text-blue-600 font-semibold">$1</span>') }} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 ">
              <h3 className="font-semibold text-gray-800 mb-4">Category Performance Highlights</h3>
              <ul className="space-y-3">
                {performanceHighlights.map((finding, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span dangerouslySetInnerHTML={{ __html: finding.value.replace(new RegExp(`(${finding.keywords})`, 'g'), '<span class="text-blue-600 font-semibold">$1</span>') }} />
                  </li>
                ))}
              </ul>
            </div>

          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-blue-600 mb-6">Query status change analysis</h2>

            <div className="mb-6">
                             <div className="bg-white  p-6">
              
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={barChart.map(item => ({
                    ...item,
                    model1Rate: typeof item.model1Rate === 'object' ? item.model1Rate.parsedValue : item.model1Rate,
                    model2Rate: typeof item.model2Rate === 'object' ? item.model2Rate.parsedValue : item.model2Rate,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(value) => `${value}%`}
                  />
                  
                  <Bar dataKey="model1Rate" fill={reportData.waveChart.model1Color} name={reportData.waveChart.model1Name} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="model2Rate" fill={reportData.waveChart.model2Color} name={reportData.waveChart.model2Name} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          
            </div>

            <div className="flex items-center justify-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{backgroundColor: reportData.waveChart.model1Color}}></div>
                <span className="text-sm text-gray-600">{reportData.waveChart.model1Name}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{backgroundColor: reportData.waveChart.model2Color}}></div>
                <span className="text-sm text-gray-600">{reportData.waveChart.model2Name}</span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Status Change Insights</h3>
              <ul className="space-y-3">
                {statusInsights.map((finding, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span dangerouslySetInnerHTML={{ __html: finding.value.replace(new RegExp(`(${finding.keywords})`, 'g'), '<span class="text-blue-600 font-semibold">$1</span>') }} />
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

      

        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-2xl font-semibold text-blue-600 mb-6">Sample Queries with Status Change</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Query
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {pageTitle.model1Name} 
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {pageTitle.model2Name} 
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sampleQueries.map((query, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 max-w-sm truncate" title={query.query}>
                      {query.query}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {query.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      <div className={`flex items-center gap-2 ${query.model1Result === 'safe' ? 'text-green-600' : 'text-red-600'}`}>
                        {query.model1Result === 'safe' ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        <span>{query.model1Result}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      <div className={`flex items-center gap-2 ${query.model2Result === 'safe' ? 'text-green-600' : 'text-red-600'}`}>
                        {query.model2Result === 'safe' ? (
                          <CheckCircle size={18} />
                        ) : (
                          <XCircle size={18} />
                        )}
                        <span>{query.model2Result}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-blue-600">Select category to display detailed conversation records</h2>
            <select 
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[250px]"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              {dialogComparison.categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          {currentComparison ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DetailCard modelName={pageTitle.model1Name} data={currentComparison.modelA} modelKey="A" />
              <DetailCard modelName={pageTitle.model2Name} data={currentComparison.modelB} modelKey="B" />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-10">Select a category to see details.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbReportDetail;